import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD, fmtDate, fmtCarat, fmtSize, PARTNER_LABELS, PARTNER_KEYS, SHAPES, buildParcelCode, computeSize, JEWELRY_TYPES, GOLD_KARATS, GOLD_COLORS } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Layers, Gem, Plus, Trash2, Receipt, Pencil } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { FileUpload } from "@/components/FileUpload";
import { FileGallery, FileLink } from "@/components/FileGallery";
import SaleActionPanel from "@/components/SaleActionPanel";
import { useAuth } from "@/context/AuthContext";

export default function ParcelDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { canWrite } = useAuth();
    const [p, setP] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/parcels/${id}`);
            setP(data);
        } catch { toast.error("Failed to load parcel"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [id]);

    if (loading) return <div className="text-stone-500 text-sm">Loading…</div>;
    if (!p) return <div className="text-stone-500">Not found</div>;

    const usedCarat = (p.children || []).reduce((s, c) => s + c.total_carat, 0);
    const usedStones = (p.children || []).reduce((s, c) => s + c.number_of_stones, 0);
    const usedCost = (p.children || []).reduce((s, c) => s + c.total_cost, 0);
    const canSplit = p.status === "available" && p.total_carat > 0;
    const canMakeJewelry = p.status === "available";
    const canSellAsIs = p.status === "available";
    const isSold = p.status === "sold";
    const sale = (p.sales || [])[0];
    const wasSplit = (p.children || []).length > 0 || p.status === "split";

    return (
        <div className="space-y-6 animate-fade-up" data-testid="parcel-detail-page">
            <Link to="/parcels" className="text-xs uppercase tracking-widest text-stone-500 hover:text-emerald-700 inline-flex items-center gap-1" data-testid="back-to-parcels">
                <ArrowLeft className="h-3 w-3" /> Parcels
            </Link>

            {/* Header text */}
            <div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Parcel</div>
                    <StatusBadge
                        status={p.status}
                        kind="parcel"
                        testid="parcel-status-badge"
                        linkTo={(p.status === "in_production" || p.status === "set_in_jewelry") && p.current_jewelry_id ? `/jewelry/${p.current_jewelry_id}` : null}
                    />
                    {!p.parent_id && (
                        <span className="text-[9px] uppercase tracking-widest text-amber-700 border border-amber-500/40 bg-amber-50 px-1.5 py-0.5" data-testid="parcel-root-badge">
                            ★ Root
                        </span>
                    )}
                    {p.parent && (
                        <Link to={`/parcels/${p.parent.id}`} className="text-[9px] uppercase tracking-widest text-sky-700 hover:text-sky-700 border border-sky-500/40 px-1.5 py-0.5" data-testid="parent-link">
                            Child of {p.parent.code}
                        </Link>
                    )}
                    {p.root && p.root.id !== p.parent?.id && (
                        <Link to={`/parcels/${p.root.id}`} className="text-[9px] uppercase tracking-widest text-amber-800 hover:text-amber-800 border border-amber-500/50 bg-amber-50/50 px-1.5 py-0.5" data-testid="root-link" title={`Original parcel: ${p.root.code}`}>
                            ★ Root {p.root.code}
                        </Link>
                    )}
                </div>
                <h1 className="font-display text-4xl sm:text-5xl leading-none mt-2 font-mono" data-testid="parcel-code">{p.code}</h1>
                {p.serial && (
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1" data-testid="parcel-serial">
                        Immutable ID · <span className="font-mono text-stone-700">#{p.serial}</span>
                    </div>
                )}
                {p.name && <div className="text-lg text-stone-500 mt-1">{p.name}</div>}
                <div className="text-sm text-stone-500 mt-2">
                    {p.shape} · {p.clarity} · {p.color}
                    {p.number_of_stones !== null && p.number_of_stones !== undefined ? ` · ${p.number_of_stones} stone${p.number_of_stones === 1 ? "" : "s"}` : " · stones unknown"}
                    {p.mm ? ` · ${p.mm}` : ""}
                    {" · purchased "}{fmtDate(p.purchase_date)}
                </div>
                {p.root && (
                    <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-500/40 mt-3 px-3 py-2 inline-flex items-center gap-2 max-w-full" data-testid="parcel-root-info">
                        <span className="text-[10px] uppercase tracking-widest text-amber-700">Original parcel</span>
                        <Link to={`/parcels/${p.root.id}`} className="font-mono text-stone-800 hover:text-emerald-700">{p.root.code}</Link>
                        {p.root.original_carat != null && <span className="num text-stone-700">· {p.root.original_carat.toFixed(2)} ct</span>}
                        {p.root.original_stones != null && <span className="text-stone-600">· {p.root.original_stones} stones</span>}
                        {p.root.original_cost != null && <span className="text-stone-600">· {fmtUSD(p.root.original_cost)}</span>}
                        {p.root.purchase_date && <span className="text-stone-500">· purchased {fmtDate(p.root.purchase_date)}</span>}
                    </div>
                )}
                {wasSplit && p.original_code && p.original_code !== p.code && (
                    <div className="text-[11px] text-stone-500 mt-1" data-testid="parcel-original-info">
                        First split as <span className="font-mono text-stone-700">{p.original_code}</span>
                        {" · "}{p.original_carat ? `${p.original_carat.toFixed(2)} ct` : ""}
                        {" · "}{p.original_stones ? `${p.original_stones} stones` : ""}
                        {p.original_cost ? ` · ${fmtUSD(p.original_cost)}` : ""}
                    </div>
                )}
                {p.notes && <p className="text-stone-500 mt-4 max-w-2xl text-sm leading-relaxed">{p.notes}</p>}
                {p.invoice && (
                    <div className="mt-3">
                        <FileLink file={p.invoice} label={`Purchase invoice — ${p.invoice.original_filename}`} testid="parcel-invoice-link" />
                    </div>
                )}
            </div>

            {(p.photos?.length > 0) && (
                <FileGallery files={p.photos} label="Photos" testid="parcel-photos-gallery" />
            )}

            {/* Horizontal stats strip — replaces the tall right sidebar so nothing can leave a blank gap */}
            <div className="border border-stone-200 bg-white grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-7 divide-x divide-y sm:divide-y-0 divide-stone-200" data-testid="parcel-info-card">
                <StatCell label="Total carat" value={fmtCarat(p.total_carat)} testid="parcel-total-carat" />
                <StatCell label="Stones" value={p.number_of_stones ?? "—"} testid="parcel-stones" />
                <StatCell label="Size (ct/st)" value={fmtSize(p.size)} testid="parcel-size" />
                <StatCell label="MM" value={p.mm || "—"} testid="parcel-mm" />
                <StatCell label="Price / ct" value={fmtUSD(p.price_per_carat)} testid="parcel-ppc" />
                {p.list_price_per_carat != null && p.list_price_per_carat !== "" && (
                    <StatCell label="List / ct" value={fmtUSD(p.list_price_per_carat)} testid="parcel-list-ppc" sub={fmtUSD(Number(p.total_carat) * Number(p.list_price_per_carat)) + " total"} />
                )}
                <StatCell label="Total cost" value={fmtUSD(p.total_cost)} accent testid="parcel-total" />
            </div>

            {/* Funding + location strip — each parcel tracks its own supplier balance */}
            {p.payments_summary && (() => {
                const s = p.payments_summary;
                return (
                    <div className="border border-stone-200 bg-white grid grid-cols-2 sm:grid-cols-4 divide-x divide-stone-200" data-testid="parcel-funding-strip">
                        <StatCell label="Bashari paid" value={fmtUSD(s.bashari)} testid="parcel-funded-bashari" />
                        <StatCell label="Ido paid" value={fmtUSD(s.ido)} testid="parcel-funded-ido" />
                        {s.supplier_outstanding > 0.01
                            ? <StatCell label="Supplier outstanding" value={fmtUSD(s.supplier_outstanding)} accent="rose" testid="parcel-supplier-outstanding" sub={`${s.status}`} />
                            : <StatCell label="Fully paid to supplier" value="✓" accent testid="parcel-fully-funded" />
                        }
                        <StatCell label="Supplier" value={p.supplier || "—"} testid="parcel-supplier" />
                    </div>
                );
            })()}

            {/* Actions */}
            {canWrite && (
                <div className="flex flex-wrap gap-3" data-testid="parcel-actions">
                    <EditParcelDialog parcel={p} onDone={load} />
                    <SplitDialog parcel={p} usedCarat={usedCarat} usedStones={usedStones} usedCost={usedCost} onDone={load} disabled={!canSplit} />
                    <MakeJewelryDialog parcel={p} onDone={(jid) => { load(); if (jid) navigate(`/jewelry/${jid}`); }} disabled={!canMakeJewelry} />
                </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue={isSold ? "sale" : (canSellAsIs ? "sale" : "children")}>
                <TabsList className="bg-transparent border-b border-stone-200 rounded-none w-full justify-start p-0 h-auto overflow-x-auto">
                    <TabTrigger value="children" testid="tab-children">Sub-parcels ({(p.children || []).length})</TabTrigger>
                    <TabTrigger value="jewelry" testid="tab-jewelry">Jewelry ({(p.jewelry || []).length})</TabTrigger>
                    <TabTrigger value="payments" testid="tab-payments">Payments ({(p.payments || []).length})</TabTrigger>
                    <TabTrigger value="expenses" testid="tab-expenses">Expenses ({(p.expenses || []).length})</TabTrigger>
                    {(canSellAsIs || isSold) && <TabTrigger value="sale" testid="tab-sale">Sell as-is</TabTrigger>}
                </TabsList>

                <TabsContent value="children" className="mt-6">
                    <ChildrenList parcel={p} onChanged={load} />
                </TabsContent>
                <TabsContent value="jewelry" className="mt-6">
                    <JewelryList parcel={p} />
                </TabsContent>
                <TabsContent value="payments" className="mt-6">
                    <PaymentsSection parcel={p} onChanged={load} />
                </TabsContent>
                <TabsContent value="expenses" className="mt-6">
                    <ExpenseSection targetType="parcel" targetId={p.id} expenses={p.expenses || []} onChanged={load} />
                </TabsContent>
                {(canSellAsIs || isSold) && (
                    <TabsContent value="sale" className="mt-6">
                        <SaleActionPanel target_type="parcel" target_id={p.id} existingSale={sale} onChanged={load} />
                    </TabsContent>
                )}
            </Tabs>
        </div>
    );
}

const TabTrigger = ({ value, children, testid }) => (
    <TabsTrigger value={value} data-testid={testid}
        className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:bg-transparent data-[state=active]:text-stone-900 text-stone-500 uppercase text-xs tracking-widest px-4 py-3">
        {children}
    </TabsTrigger>
);

const StatCell = ({ label, value, sub, accent, testid }) => (
    <div className="px-4 py-3">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className={`num text-base mt-0.5 ${accent === "rose" ? "text-rose-600" : accent ? "text-emerald-700" : "text-stone-900"}`} data-testid={testid}>{value}</div>
        {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
);

const Row = ({ label, value, accent, testid }) => (
    <div className="flex items-center justify-between py-1 border-b last:border-b-0 border-stone-200/70">
        <span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span>
        <span className={`num text-sm ${accent ? "text-emerald-700" : "text-stone-900"}`} data-testid={testid}>{value}</span>
    </div>
);

// -------------------- Children (sub-parcels) --------------------
const ChildrenList = ({ parcel, onChanged }) => {
    const { canWrite } = useAuth();
    const mergeChild = async (child) => {
        if (!confirm(`Merge ${child.code} back into ${parcel.code}? Its ${child.total_carat.toFixed(2)} ct will be added back to the parent and the sub-parcel deleted.`)) return;
        try {
            await api.post(`/parcels/${parcel.id}/merge`, { child_id: child.id });
            toast.success("Sub-parcel merged back into parent");
            onChanged?.();
        } catch (err) { toast.error(err?.response?.data?.detail || "Merge failed"); }
    };

    if (!(parcel.children || []).length) {
        return (
            <div className="border border-stone-200 p-8 text-center bg-[#f5efe0]">
                <Layers className="mx-auto h-6 w-6 text-stone-500" strokeWidth={1.2} />
                <p className="text-sm text-stone-500 mt-3">No sub-parcels yet. Split this parcel to create them.</p>
            </div>
        );
    }
    return (
        <div className="border border-stone-200 overflow-x-auto" data-testid="children-table">
            <table className="w-full text-sm min-w-[720px]">
                <thead>
                    <tr className="border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500 text-left">
                        <th className="py-3 px-4">Code</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-right">Carat</th>
                        <th className="py-3 px-4 text-right">Stones</th>
                        <th className="py-3 px-4">MM</th>
                        <th className="py-3 px-4 text-right">$/ct</th>
                        <th className="py-3 px-4 text-right">Cost</th>
                        <th className="py-3 px-4"></th>
                    </tr>
                </thead>
                <tbody>
                    {parcel.children.map(c => (
                        <tr key={c.id} className="border-b border-stone-200/70 hover:bg-stone-100" data-testid={`child-row-${c.id}`}>
                            <td className="py-3 px-4">
                                <Link to={`/parcels/${c.id}`} className="font-mono text-stone-900 hover:text-emerald-700">{c.code}</Link>
                                {c.name && <div className="text-[11px] text-stone-500">{c.name}</div>}
                            </td>
                            <td className="py-3 px-4"><StatusBadge status={c.status} kind="parcel" linkTo={(c.status === "in_production" || c.status === "set_in_jewelry") && c.current_jewelry_id ? `/jewelry/${c.current_jewelry_id}` : null} /></td>
                            <td className="py-3 px-4 text-right num">{fmtCarat(c.total_carat)}</td>
                            <td className="py-3 px-4 text-right num text-stone-700">{c.number_of_stones ?? "—"}</td>
                            <td className="py-3 px-4 text-stone-500 text-xs">{c.mm || "—"}</td>
                            <td className="py-3 px-4 text-right num text-stone-700">{fmtUSD(c.price_per_carat)}</td>
                            <td className="py-3 px-4 text-right num text-stone-900">{fmtUSD(c.total_cost)}</td>
                            <td className="py-3 px-4 text-right">
                                {canWrite && (
                                    <button
                                        onClick={() => mergeChild(c)}
                                        disabled={c.status !== "available"}
                                        className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-emerald-700 disabled:opacity-30 disabled:hover:text-stone-500"
                                        data-testid={`merge-child-${c.id}`}
                                        title={c.status !== "available" ? "Only available sub-parcels can be merged back" : "Merge back into parent"}
                                    >
                                        ← merge
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const JewelryList = ({ parcel }) => {
    if (!(parcel.jewelry || []).length) {
        return (
            <div className="border border-stone-200 p-8 text-center bg-[#f5efe0]">
                <Gem className="mx-auto h-6 w-6 text-stone-500" strokeWidth={1.2} />
                <p className="text-sm text-stone-500 mt-3">No jewelry made from this parcel.</p>
            </div>
        );
    }
    return (
        <div className="border border-stone-200 overflow-x-auto" data-testid="jewelry-list">
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500 text-left">
                        <th className="py-3 px-4">Name / Type</th>
                        <th className="py-3 px-4">Gold</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-right">Allocated cost</th>
                    </tr>
                </thead>
                <tbody>
                    {parcel.jewelry.map(j => (
                        <tr key={j.id} className="border-b border-stone-200/70 hover:bg-stone-100">
                            <td className="py-3 px-4">
                                <Link to={`/jewelry/${j.id}`} className="text-stone-900 hover:text-emerald-700">{j.name || j.jewelry_type}</Link>
                                <div className="text-[11px] text-stone-500">{j.jewelry_type}</div>
                            </td>
                            <td className="py-3 px-4 text-stone-700 text-xs">{j.gold_karat}k {j.gold_color} · {j.gold_weight_gr}g{j.gross_weight_gr != null ? ` · gross ${j.gross_weight_gr}g` : ""}</td>
                            <td className="py-3 px-4"><StatusBadge status={j.status} kind="jewelry" /></td>
                            <td className="py-3 px-4 text-right num">{fmtUSD(j.allocated_cost)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// -------------------- Split dialog --------------------
const SplitDialog = ({ parcel, usedCarat, usedStones, usedCost, onDone, disabled }) => {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([emptyRow(parcel)]);
    const [saving, setSaving] = useState(false);

    // Parent's current values ALREADY reflect any previous splits.
    const remainingCarat = parcel.total_carat;
    const remainingStones = parcel.number_of_stones;  // may be null

    const rowsCarat = rows.reduce((s, r) => s + Number(r.total_carat || 0), 0);
    const rowsStones = rows.reduce((s, r) => s + Number(r.number_of_stones || 0), 0);
    const anyRowHasStones = rows.some(r => r.number_of_stones !== "" && r.number_of_stones !== null && r.number_of_stones !== undefined);
    const rowsCost = rows.reduce((s, r) => s + Number(r.total_carat || 0) * Number(r.price_per_carat || parcel.price_per_carat), 0);
    // Sub costs must sum to (moved carats) × parent $/ct
    const requiredCost = +(rowsCarat * parcel.price_per_carat).toFixed(2);
    const costDelta = +(rowsCost - requiredCost).toFixed(2);

    const validCarat = rowsCarat <= remainingCarat + 0.0001 && rowsCarat > 0;
    const validStones = remainingStones === null || remainingStones === undefined
        ? true
        : (!anyRowHasStones || rowsStones <= remainingStones);
    const validCost = Math.abs(costDelta) < 0.01;
    const canSubmit = rows.every(r => r.total_carat) && validCarat && validStones && validCost;

    const upd = (i, patch) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
    const add = () => setRows(rs => [...rs, emptyRow(parcel)]);
    const rem = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));

    const submit = async () => {
        if (!canSubmit) { toast.error("Fix the highlighted issues first"); return; }
        setSaving(true);
        try {
            await api.post(`/parcels/${parcel.id}/split`, {
                sub_parcels: rows.map(r => ({
                    name: r.name || null,
                    shape: r.shape || null,
                    clarity: r.clarity || null,
                    color: r.color || null,
                    total_carat: Number(r.total_carat),
                    number_of_stones: r.number_of_stones === "" || r.number_of_stones === null ? null : Number(r.number_of_stones),
                    mm: r.mm || null,
                    price_per_carat: r.price_per_carat ? Number(r.price_per_carat) : null,
                    location: r.location || null,
                    notes: r.notes || null,
                })),
            });
            toast.success(`Created ${rows.length} sub-parcel(s); parent reduced`);
            setOpen(false); setRows([emptyRow(parcel)]); onDone();
        } catch (err) { toast.error(err?.response?.data?.detail || "Split failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button disabled={disabled} data-testid="split-parcel-button" className="rounded-none bg-stone-900 text-stone-50 hover:bg-stone-800 disabled:opacity-40">
                    <Layers className="h-4 w-4 mr-2" /> Split into sub-parcels
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#faf7ef] border-stone-200 rounded-none max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="split-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">Split parcel</DialogTitle>
                </DialogHeader>
                <div className="text-xs text-stone-500 border border-stone-200 bg-white p-3">
                    Parent has <span className="text-stone-700 num">{remainingCarat.toFixed(2)} ct</span>
                    {remainingStones !== null && remainingStones !== undefined
                        ? <>, <span className="text-stone-700 num">{remainingStones}</span> stones</>
                        : <>, <span className="text-stone-500">stones unknown</span></>} remaining.
                    Whatever you split off will be <span className="text-stone-700">reduced from the parent</span>. Sub-parcel prices must average to parent&apos;s <span className="text-stone-700 num">{fmtUSD(parcel.price_per_carat)}/ct</span>.
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs">
                    <RemBox label="Carats moving" value={`${rowsCarat.toFixed(2)} / ${remainingCarat.toFixed(2)}`} bad={!validCarat} testid="split-rem-carat" />
                    <RemBox label="Stones moving" value={remainingStones !== null && remainingStones !== undefined ? `${rowsStones} / ${remainingStones}` : (anyRowHasStones ? `${rowsStones} / —` : "—")} bad={!validStones} testid="split-rem-stones" />
                    <RemBox label={`Cost must equal ${fmtUSD(requiredCost)}`} value={`${fmtUSD(rowsCost)} (${costDelta >= 0 ? "+" : ""}${fmtUSD(costDelta)})`} bad={!validCost} testid="split-rem-cost" />
                </div>
                <div className="space-y-3">
                    {rows.map((r, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 border border-stone-200 p-3 items-start">
                            <div className="col-span-12 md:col-span-3">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Name (opt)</Label>
                                <Input value={r.name} onChange={e => upd(i, { name: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid={`split-name-${i}`} />
                            </div>
                            <div className="col-span-6 md:col-span-2">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Shape</Label>
                                <Select value={r.shape} onValueChange={v => upd(i, { shape: v })}>
                                    <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid={`split-shape-${i}`}><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none max-h-64">
                                        {SHAPES.map(s => <SelectItem key={s.value} value={s.value}>{s.code}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="col-span-6 md:col-span-1">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Clr</Label>
                                <Input value={r.clarity} onChange={e => upd(i, { clarity: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid={`split-clarity-${i}`} />
                            </div>
                            <div className="col-span-6 md:col-span-1">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Col</Label>
                                <Input value={r.color} onChange={e => upd(i, { color: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid={`split-color-${i}`} />
                            </div>
                            <div className="col-span-6 md:col-span-1">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Carat</Label>
                                <Input type="number" step="0.01" min="0" value={r.total_carat} onChange={e => upd(i, { total_carat: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid={`split-carat-${i}`} />
                            </div>
                            <div className="col-span-6 md:col-span-1">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">Stones</Label>
                                <Input type="number" step="1" min="1" value={r.number_of_stones} onChange={e => upd(i, { number_of_stones: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid={`split-stones-${i}`} placeholder="opt" />
                            </div>
                            <div className="col-span-6 md:col-span-2">
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1 block">$/ct (opt)</Label>
                                <Input type="number" step="0.01" min="0" value={r.price_per_carat} placeholder={String(parcel.price_per_carat)} onChange={e => upd(i, { price_per_carat: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid={`split-ppc-${i}`} />
                            </div>
                            <div className="col-span-11 md:col-span-1 flex items-end justify-between">
                                <div className="text-xs num text-emerald-700" data-testid={`split-cost-${i}`}>
                                    {fmtUSD(Number(r.total_carat || 0) * Number(r.price_per_carat || parcel.price_per_carat))}
                                </div>
                            </div>
                            <button type="button" onClick={() => rem(i)} disabled={rows.length === 1}
                                className="col-span-1 flex text-stone-400 hover:text-rose-600 items-end disabled:opacity-30" data-testid={`split-remove-${i}`}>
                                <Trash2 className="h-4 w-4" />
                            </button>
                            <div className="col-span-12 -mt-1">
                                <PreviewCode row={r} parcel={parcel} testid={`split-preview-code-${i}`} />
                            </div>
                        </div>
                    ))}
                </div>
                <Button variant="ghost" onClick={add} className="rounded-none text-stone-500 hover:text-stone-900 mt-2" data-testid="split-add-row">
                    <Plus className="h-4 w-4 mr-1" /> Add another sub-parcel
                </Button>
                <DialogFooter>
                    <Button onClick={submit} disabled={saving || !canSubmit} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="split-submit">
                        {saving ? "Creating…" : "Create sub-parcels"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const emptyRow = (parcel) => ({
    name: "", shape: parcel.shape, clarity: parcel.clarity, color: parcel.color,
    total_carat: "", number_of_stones: "", mm: parcel.mm || "", price_per_carat: "", location: "", notes: "",
});

const PreviewCode = ({ row, parcel, testid }) => {
    const code = buildParcelCode({
        shape: row.shape || parcel.shape,
        clarity: row.clarity || parcel.clarity,
        color: row.color || parcel.color,
    });
    return <div className="font-mono text-[11px] text-stone-500" data-testid={testid}>Preview: <span className="text-emerald-700">{code}</span></div>;
};

const RemBox = ({ label, value, bad, testid }) => (
    <div className={`border p-2 ${bad ? "border-rose-500/70 bg-rose-50 text-rose-700" : "border-stone-200 bg-white text-stone-500"}`} data-testid={testid}>
        <div className="text-[10px] uppercase tracking-widest">{label}</div>
        <div className="num text-xs mt-1">{value}</div>
    </div>
);

// -------------------- Jewelry form (create + edit) --------------------
const GRAMS_PER_TROY_OUNCE = 31.1034768;

const emptyJewelryForm = () => ({
    jewelry_type: "Ring", gold_karat: 18, gold_weight_gr: "", gross_weight_gr: "", gold_color: "Yellow",
    gold_price_per_oz: "", labor_cost: "", production_paid_by: "bashari",
    name: "", description: "", location: "", photos: [],
});

const jewelryToForm = (j) => ({
    jewelry_type: j.jewelry_type || "Ring",
    gold_karat: j.gold_karat || 18,
    gold_weight_gr: j.gold_weight_gr ?? "",
    gross_weight_gr: j.gross_weight_gr ?? "",
    gold_color: j.gold_color || "Yellow",
    gold_price_per_oz: j.gold_price_per_oz ?? "",
    labor_cost: j.labor_cost ?? "",
    production_paid_by: j.production_paid_by || "bashari",
    name: j.name || "",
    description: j.description || "",
    location: j.location || "",
    photos: j.photos || [],
    // preserve original gold_cost so we can show it when the piece is in manual fallback (no price set)
    _existingGoldCost: j.gold_cost ?? null,
});

const MakeJewelryDialog = ({ parcel, onDone, disabled }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button disabled={disabled} onClick={() => setOpen(true)} data-testid="make-jewelry-button" className="rounded-none border border-emerald-500/50 text-emerald-800 bg-emerald-50 hover:bg-emerald-50 disabled:opacity-40">
                <Gem className="h-4 w-4 mr-2" /> Set into jewelry
            </Button>
            <JewelryFormDialog open={open} onOpenChange={setOpen} parcel={parcel} onDone={onDone} />
        </>
    );
};

export const JewelryFormDialog = ({ open, onOpenChange, parcel, existing, onDone }) => {
    const isEdit = !!existing;
    const parcelInfo = existing?.parcel_snapshot || parcel;
    const parcelCost = parcelInfo?.total_cost ?? 0;
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(emptyJewelryForm());

    useEffect(() => {
        if (!open) return;
        setForm(isEdit ? jewelryToForm(existing) : emptyJewelryForm());
    }, [open, isEdit, existing]);

    // Auto-compute gold cost from purity × weight × spot price (troy oz). Manual fallback: for old
    // pieces that never had a price locked in, if the field is left blank we preserve the existing
    // gold_cost value as-is.
    const priceNum = Number(form.gold_price_per_oz || 0);
    const weightNum = Number(form.gold_weight_gr || 0);
    const karatNum = Number(form.gold_karat || 0);
    const pureGrams = weightNum > 0 && karatNum > 0 ? (weightNum * karatNum) / 24 : 0;
    const pureOz = pureGrams / GRAMS_PER_TROY_OUNCE;
    const computedGoldCost = priceNum > 0 && pureGrams > 0 ? Math.round(pureOz * priceNum * 100) / 100 : null;
    const manualFallbackGoldCost = computedGoldCost == null && form._existingGoldCost != null ? Number(form._existingGoldCost) : 0;
    const effectiveGoldCost = computedGoldCost != null ? computedGoldCost : manualFallbackGoldCost;
    const laborCostNum = Number(form.labor_cost || 0);
    const productionNeeded = effectiveGoldCost > 0 || laborCostNum > 0;
    const hardCostTotal = Number(parcelCost || 0) + effectiveGoldCost + laborCostNum;

    const submit = async (e) => {
        e.preventDefault();
        if (productionNeeded && !form.production_paid_by) {
            toast.error("Pick who paid for the gold + labor");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                jewelry_type: form.jewelry_type,
                gold_karat: Number(form.gold_karat),
                gold_weight_gr: Number(form.gold_weight_gr),
                gross_weight_gr: form.gross_weight_gr === "" ? null : Number(form.gross_weight_gr),
                gold_color: form.gold_color,
                gold_price_per_oz: form.gold_price_per_oz === "" ? null : Number(form.gold_price_per_oz),
                // gold_cost is intentionally omitted — backend computes it from price when supplied,
                // otherwise preserves the existing (manual) value.
                labor_cost: form.labor_cost === "" ? null : Number(form.labor_cost),
                production_paid_by: productionNeeded ? form.production_paid_by : null,
                name: form.name || null,
                description: form.description || null,
                location: form.location || null,
                photos: form.photos || [],
            };
            if (isEdit) {
                const { data } = await api.patch(`/jewelry/${existing.id}`, payload);
                toast.success("Jewelry updated");
                onOpenChange(false);
                onDone?.(data.id || existing.id);
            } else {
                const { data } = await api.post("/jewelry", { ...payload, parcel_id: parcel.id });
                toast.success("Jewelry created");
                onOpenChange(false);
                onDone?.(data.id);
            }
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#faf7ef] border-stone-200 rounded-none max-w-xl max-h-[90vh] overflow-y-auto" data-testid="make-jewelry-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">{isEdit ? "Edit jewelry" : "Make jewelry from this parcel"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    {isEdit ? (
                        <div className="text-xs text-stone-500 border border-stone-200 bg-white p-3">
                            Source parcel <span className="font-mono text-stone-800">{parcelInfo?.code}</span> · allocated {fmtUSD(parcelCost)} (fixed).
                        </div>
                    ) : (
                        <div className="text-xs text-stone-500 border border-stone-200 bg-white p-3">
                            The full parcel cost ({fmtUSD(parcelCost)}) transfers to the new jewelry as its allocated cost. Parcel status → In Production.
                        </div>
                    )}
                    <Field label="Name (optional)"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="jewelry-name-input" /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Jewelry type">
                            <Select value={form.jewelry_type} onValueChange={v => setForm({ ...form, jewelry_type: v })}>
                                <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="jewelry-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none">
                                    {JEWELRY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Gold color">
                            <Select value={form.gold_color} onValueChange={v => setForm({ ...form, gold_color: v })}>
                                <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="jewelry-goldcolor-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none">
                                    {GOLD_COLORS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Gold karat">
                            <Select value={String(form.gold_karat)} onValueChange={v => setForm({ ...form, gold_karat: Number(v) })}>
                                <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="jewelry-goldkarat-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none">
                                    {GOLD_KARATS.map(k => <SelectItem key={k} value={String(k)}>{k}k</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Gold weight (gr)"><Input required type="number" step="0.01" min="0" value={form.gold_weight_gr} onChange={e => setForm({ ...form, gold_weight_gr: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="jewelry-goldweight-input" /></Field>
                    </div>
                    <Field label="Gross weight (gr) — optional"><Input type="number" step="0.01" min="0" value={form.gross_weight_gr} onChange={e => setForm({ ...form, gross_weight_gr: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="jewelry-grossweight-input" placeholder="total finished piece: gold + stones + setting" /></Field>

                    <div className="border-t border-stone-200 pt-3 mt-3">
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Hard cost (added to allocated stones cost)</div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Gold price per ounce (USD)">
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm select-none">$</span>
                                    <Input type="number" step="0.01" min="0" value={form.gold_price_per_oz} onChange={e => setForm({ ...form, gold_price_per_oz: e.target.value })} className="rounded-none bg-white border-stone-200 pl-6" data-testid="jewelry-goldprice-input" placeholder="0.00" />
                                </div>
                            </Field>
                            <Field label="Labor cost (USD)"><Input type="number" step="0.01" min="0" value={form.labor_cost} onChange={e => setForm({ ...form, labor_cost: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="jewelry-laborcost-input" placeholder="0.00" /></Field>
                        </div>
                        {(computedGoldCost != null || (isEdit && form._existingGoldCost != null && Number(form._existingGoldCost) > 0)) && (
                            <div className="mt-2 text-[11px] text-stone-600 bg-stone-50 border border-stone-200 px-3 py-2" data-testid="jewelry-gold-preview">
                                {computedGoldCost != null ? (
                                    <>
                                        @ <span className="text-stone-800">{karatNum}k</span> · <span className="num text-stone-800">{weightNum.toFixed(2)} g</span> total →{" "}
                                        <span className="num text-stone-800">{pureGrams.toFixed(2)} g</span> pure (<span className="num text-stone-800">{pureOz.toFixed(4)} oz</span>) · gold cost{" "}
                                        <span className="num text-emerald-700">{fmtUSD(computedGoldCost)}</span>
                                    </>
                                ) : (
                                    <>
                                        Manual gold cost: <span className="num text-stone-800">{fmtUSD(Number(form._existingGoldCost))}</span>
                                        <span className="text-stone-500"> — enter a price above to switch to auto-calc.</span>
                                    </>
                                )}
                            </div>
                        )}
                        {productionNeeded && (
                            <div className="mt-3">
                                <Field label="Paid by (for gold + labor)">
                                    <Select value={form.production_paid_by} onValueChange={v => setForm({ ...form, production_paid_by: v })}>
                                        <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="jewelry-production-paidby-select"><SelectValue /></SelectTrigger>
                                        <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none">
                                            {PARTNER_KEYS.map(pk => <SelectItem key={pk} value={pk}>{PARTNER_LABELS[pk]}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>
                        )}
                        <div className="mt-3 border border-stone-200 bg-white p-3 flex items-center justify-between" data-testid="jewelry-hardcost-preview">
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">Total hard cost</div>
                            <div className="num text-stone-900 text-lg">{fmtUSD(hardCostTotal)}</div>
                        </div>
                    </div>

                    <Field label="Location"><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="jewelry-location-input" /></Field>
                    <Field label="Description"><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="jewelry-description-input" /></Field>
                    <Field label="Photos (optional)">
                        <FileUpload
                            value={form.photos}
                            onChange={photos => setForm({ ...form, photos })}
                            accept="image/*"
                            label="Upload jewelry photos"
                            testid="jewelry-photos"
                        />
                    </Field>
                    <DialogFooter>
                        <Button type="submit" disabled={saving} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-jewelry-button">
                            {saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create jewelry")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, children }) => (
    <div><Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 block">{label}</Label>{children}</div>
);

// -------------------- Edit Parcel dialog --------------------
const EditParcelDialog = ({ parcel, onDone }) => {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [suppliers, setSuppliers] = useState([]);
    const isRoot = !parcel.parent_id;
    const hasChildren = (parcel.children || []).length > 0;

    useEffect(() => {
        if (!open) return;
        api.get("/parcels/suppliers").then(({ data }) => setSuppliers(data?.suppliers || [])).catch(() => { });
    }, [open]);
    const [form, setForm] = useState(() => ({
        name: parcel.name || "",
        shape: parcel.shape,
        clarity: parcel.clarity,
        color: parcel.color,
        total_carat: parcel.total_carat,
        number_of_stones: parcel.number_of_stones ?? "",
        size: parcel.size ?? "",
        mm: parcel.mm || "",
        price_per_carat: parcel.price_per_carat,
        list_price_per_carat: parcel.list_price_per_carat ?? "",
        supplier: parcel.supplier || "",
        location: parcel.location || "",
        notes: parcel.notes || "",
        purchase_date: parcel.purchase_date || "",
        photos: parcel.photos || [],
        invoice: parcel.invoice || null,
    }));

    const carat = Number(form.total_carat || 0);
    const ppc = Number(form.price_per_carat || 0);
    // Just a display value; the backend recomputes total_cost from carat × ppc.
    const totalPreview = +(carat * ppc).toFixed(2);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const patch = {
                name: form.name || null,
                shape: form.shape,
                clarity: form.clarity,
                color: form.color,
                total_carat: Number(form.total_carat),
                number_of_stones: form.number_of_stones === "" ? null : Number(form.number_of_stones),
                mm: form.mm || null,
                price_per_carat: Number(form.price_per_carat),
                list_price_per_carat: form.list_price_per_carat === "" || form.list_price_per_carat === null ? null : Number(form.list_price_per_carat),
                supplier: form.supplier || null,
                location: form.location || null,
                notes: form.notes || null,
                purchase_date: form.purchase_date || null,
                photos: form.photos || [],
                invoice: form.invoice || null,
            };
            if (form.size !== "" && form.size !== null) patch.size = Number(form.size);
            await api.patch(`/parcels/${parcel.id}`, patch);
            toast.success("Parcel updated");
            setOpen(false);
            onDone();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="ghost" data-testid="edit-parcel-button" className="rounded-none border border-stone-300 text-stone-800 hover:bg-stone-100">
                    <Pencil className="h-4 w-4 mr-2" strokeWidth={1.5} /> Edit
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#faf7ef] border-stone-200 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-parcel-dialog">
                <DialogHeader><DialogTitle className="font-display text-2xl">Edit parcel</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    {hasChildren && (
                        <div className="text-[11px] text-amber-700 border border-amber-500/40 bg-amber-100 px-3 py-2">
                            This parcel has sub-parcels. Changes to carat/stones/$-per-carat still apply, but the residual + children may need manual reconciliation.
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Name"><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-name-input" /></Field>
                        <Field label="Purchase date"><Input type="date" value={form.purchase_date} onChange={e => setForm({ ...form, purchase_date: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-date-input" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Shape">
                            <Select value={form.shape} onValueChange={v => setForm({ ...form, shape: v })}>
                                <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-shape-select"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none max-h-64">
                                    {SHAPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label} ({s.code})</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Number of stones"><Input type="number" min="1" step="1" value={form.number_of_stones} onChange={e => setForm({ ...form, number_of_stones: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-stones-input" placeholder="empty if unknown" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Clarity"><Input value={form.clarity} onChange={e => setForm({ ...form, clarity: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-clarity-input" /></Field>
                        <Field label="Color"><Input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-color-input" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Total carat"><Input type="number" step="0.01" min="0" value={form.total_carat} onChange={e => setForm({ ...form, total_carat: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-carat-input" /></Field>
                        <Field label="Price / carat"><Input type="number" step="0.01" min="0" value={form.price_per_carat} onChange={e => setForm({ ...form, price_per_carat: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-ppc-input" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="List price / carat (optional)"><Input type="number" step="0.01" min="0" value={form.list_price_per_carat} onChange={e => setForm({ ...form, list_price_per_carat: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-listppc-input" placeholder="recommended selling price / ct" /></Field>
                        <Field label="Size (leave blank = auto)"><Input type="number" step="0.01" value={form.size} onChange={e => setForm({ ...form, size: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-size-input" placeholder="auto" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="MM"><Input value={form.mm} onChange={e => setForm({ ...form, mm: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-mm-input" /></Field>
                        <div></div>
                    </div>
                    {isRoot && (
                        <div className="border border-stone-200 bg-white p-3 text-xs text-stone-600 leading-relaxed">
                            Partner payments to the supplier are now recorded in the <b>Payments</b> tab.
                            Editing them here has been retired so the audit trail stays clean.
                            Current total paid: <span className="num text-stone-900">{fmtUSD((parcel.payments_summary?.bashari || 0) + (parcel.payments_summary?.ido || 0))}</span>
                            {" · "}Owed to supplier: <span className="num text-rose-600">{fmtUSD(parcel.payments_summary?.supplier_outstanding || 0)}</span>
                        </div>
                    )}
                    <Field label="Supplier">
                        <Input list="edit-parcel-supplier-options" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-supplier-input" placeholder="e.g. ABC Diamonds Co." />
                        <datalist id="edit-parcel-supplier-options">
                            {suppliers.map(s => <option key={s} value={s} />)}
                        </datalist>
                    </Field>
                    <Field label="Location"><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-location-input" /></Field>
                    <Field label="Notes"><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="edit-parcel-notes-input" /></Field>
                    <Field label="Photos">
                        <FileUpload
                            value={form.photos}
                            onChange={photos => setForm({ ...form, photos })}
                            accept="image/*"
                            label="Upload photos"
                            testid="edit-parcel-photos"
                        />
                    </Field>
                    <Field label="Purchase invoice">
                        <FileUpload
                            value={form.invoice}
                            onChange={invoice => setForm({ ...form, invoice })}
                            accept="image/*,application/pdf"
                            label="Upload invoice (PDF or image)"
                            testid="edit-parcel-invoice"
                            single
                        />
                    </Field>
                    <DialogFooter>
                        <Button type="submit" disabled={saving} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-edit-parcel-button">
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

// -------------------- Payments (partner cash outflows to supplier) --------------------
const PaymentsSection = ({ parcel, onChanged }) => {
    const { canWrite } = useAuth();
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ partner: "bashari", amount: "", date: new Date().toISOString().slice(0, 10), notes: "" });
    const [reassignId, setReassignId] = useState(null);
    const [reassignTarget, setReassignTarget] = useState("");

    const s = parcel.payments_summary || { bashari: 0, ido: 0, total_paid: 0, supplier_liability: parcel.total_cost, supplier_outstanding: parcel.total_cost, status: "unpaid" };
    // Payments attach to THIS parcel (root or sub) — each has its own supplier balance.
    const targetId = parcel.id;

    const submit = async (e) => {
        e.preventDefault();
        const amt = Number(form.amount);
        if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
        setSaving(true);
        try {
            await api.post(`/parcels/${targetId}/payments`, {
                partner: form.partner,
                amount: amt,
                date: form.date,
                notes: form.notes || null,
            });
            toast.success(`Payment recorded (${PARTNER_LABELS[form.partner]} ${fmtUSD(amt)})`);
            setForm({ partner: form.partner, amount: "", date: new Date().toISOString().slice(0, 10), notes: "" });
            setOpen(false);
            onChanged();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    const remove = async (pid) => {
        if (!confirm("Delete this payment?")) return;
        try { await api.delete(`/parcels/payments/${pid}`); toast.success("Payment removed"); onChanged(); }
        catch { toast.error("Failed"); }
    };

    const reassign = async (pid) => {
        if (!reassignTarget) { toast.error("Pick a sub-parcel first"); return; }
        try {
            await api.patch(`/parcels/payments/${pid}`, { parcel_id: reassignTarget });
            toast.success("Payment reassigned");
            setReassignId(null);
            setReassignTarget("");
            onChanged();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    // Only the root's payment rows can be reassigned into children. (Legacy migration convenience.)
    const reassignTargets = (parcel.children || []).filter(c => c.status !== "sold");
    const canReassignFromHere = !parcel.parent_id && reassignTargets.length > 0;

    const statusColor = s.status === "paid" ? "text-emerald-700" : s.status === "partial" ? "text-amber-700" : "text-rose-600";

    return (
        <div className="space-y-4" data-testid="payments-section">
            {parcel.parent_id && (
                <div className="text-[11px] text-stone-500 border border-stone-200 bg-white p-3">
                    Payments here are attributed to <b>this sub-parcel only</b>. Splitting a parcel does not automatically split its payments — reassign them from the parent if needed.
                </div>
            )}
            <div className="border border-stone-200 bg-white grid grid-cols-2 sm:grid-cols-4 divide-x divide-stone-200">
                <StatCell label="Bashari paid" value={fmtUSD(s.bashari)} testid="payments-bashari-total" />
                <StatCell label="Ido paid" value={fmtUSD(s.ido)} testid="payments-ido-total" />
                <StatCell label="Total paid" value={fmtUSD(s.total_paid)} testid="payments-total-paid" sub={`of ${fmtUSD(s.supplier_liability)}`} />
                <div className="px-4 py-3">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">Supplier balance</div>
                    <div className={`num text-base mt-0.5 ${statusColor}`} data-testid="payments-supplier-outstanding">
                        {s.supplier_outstanding > 0.01 ? fmtUSD(s.supplier_outstanding) : "✓ Paid"}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">{s.status}</div>
                </div>
            </div>

            {canWrite && (
                <div className="flex justify-end">
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-none bg-stone-900 text-stone-50 hover:bg-stone-800" data-testid="add-payment-button" disabled={s.supplier_outstanding <= 0.01}>
                                <Plus className="h-4 w-4 mr-2" /> Record payment to supplier
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-[#faf7ef] border-stone-200 rounded-none max-w-lg" data-testid="add-payment-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">Record payment</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="text-xs text-stone-500 border border-stone-200 bg-white p-3">
                                    Outstanding to supplier: <span className="num text-rose-600">{fmtUSD(s.supplier_outstanding)}</span>
                                </div>
                                <Field label="Paid by">
                                    <div className="grid grid-cols-2 gap-2">
                                        {PARTNER_KEYS.map(pk => (
                                            <button key={pk} type="button" onClick={() => setForm({ ...form, partner: pk })}
                                                className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors ${form.partner === pk ? "border-emerald-600 bg-emerald-600 text-white" : "border-stone-300 bg-white text-stone-700 hover:border-emerald-600 hover:text-emerald-700"}`}
                                                data-testid={`payment-partner-${pk}`}>
                                                {PARTNER_LABELS[pk]}
                                            </button>
                                        ))}
                                    </div>
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Amount (USD)">
                                        <Input required type="number" step="0.01" min="0.01" max={s.supplier_outstanding} value={form.amount}
                                            onChange={e => setForm({ ...form, amount: e.target.value })}
                                            className="rounded-none bg-white border-stone-200" data-testid="payment-amount-input" />
                                    </Field>
                                    <Field label="Date">
                                        <Input required type="date" value={form.date}
                                            onChange={e => setForm({ ...form, date: e.target.value })}
                                            className="rounded-none bg-white border-stone-200" data-testid="payment-date-input" />
                                    </Field>
                                </div>
                                <Field label="Notes (optional)">
                                    <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                                        className="rounded-none bg-white border-stone-200" data-testid="payment-notes-input"
                                        placeholder="e.g. wire · check #4231 · Venmo" />
                                </Field>
                                <DialogFooter>
                                    <Button type="submit" disabled={saving || !form.amount} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-payment-button">
                                        {saving ? "Saving…" : "Record payment"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            )}

            {(parcel.payments || []).length === 0 ? (
                <div className="border border-stone-200 p-8 text-center bg-[#f5efe0]">
                    <Receipt className="mx-auto h-6 w-6 text-stone-500" strokeWidth={1.2} />
                    <p className="text-sm text-stone-500 mt-3">No payments recorded for this parcel yet.</p>
                </div>
            ) : (
                <div className="border border-stone-200" data-testid="payments-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500 text-left">
                                <th className="py-3 px-4">Date</th>
                                <th className="py-3 px-4">Partner</th>
                                <th className="py-3 px-4 text-right">Amount</th>
                                <th className="py-3 px-4">Notes</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {parcel.payments.map(pay => (
                                <tr key={pay.id} className="border-b border-stone-200/70 hover:bg-stone-100" data-testid={`payment-row-${pay.id}`}>
                                    <td className="py-3 px-4 text-stone-500">{fmtDate(pay.date)}</td>
                                    <td className="py-3 px-4 text-stone-800">{PARTNER_LABELS[pay.partner]}</td>
                                    <td className="py-3 px-4 text-right num text-emerald-700">{fmtUSD(pay.amount)}</td>
                                    <td className="py-3 px-4 text-stone-500 text-xs">
                                        {reassignId === pay.id ? (
                                            <span className="inline-flex items-center gap-1.5">
                                                <select
                                                    value={reassignTarget}
                                                    onChange={e => setReassignTarget(e.target.value)}
                                                    className="border border-stone-300 bg-white text-xs px-2 py-1 rounded-none"
                                                    data-testid={`reassign-target-${pay.id}`}
                                                >
                                                    <option value="">Move to…</option>
                                                    {reassignTargets.map(c => (
                                                        <option key={c.id} value={c.id}>{c.code || c.name}</option>
                                                    ))}
                                                </select>
                                                <button type="button" onClick={() => reassign(pay.id)} className="text-[10px] uppercase tracking-widest text-emerald-700 hover:text-emerald-800" data-testid={`save-reassign-${pay.id}`}>
                                                    Save
                                                </button>
                                                <button type="button" onClick={() => { setReassignId(null); setReassignTarget(""); }} className="text-[10px] uppercase tracking-widest text-stone-500 hover:text-stone-800">×</button>
                                            </span>
                                        ) : (pay.notes || "—")}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        {canWrite && (
                                            <div className="inline-flex items-center gap-2">
                                                {canReassignFromHere && reassignId !== pay.id && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { setReassignId(pay.id); setReassignTarget(""); }}
                                                        className="text-stone-400 hover:text-emerald-700 text-[10px] uppercase tracking-widest"
                                                        title="Reassign to a sub-parcel"
                                                        data-testid={`reassign-payment-${pay.id}`}
                                                    >
                                                        Reassign
                                                    </button>
                                                )}
                                                <button onClick={() => remove(pay.id)} className="text-stone-400 hover:text-rose-600" data-testid={`delete-payment-${pay.id}`}>
                                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};


// -------------------- Expenses (shared) --------------------
const emptyExpenseForm = () => ({ category: "", amount: "", paid_by: "bashari", date: new Date().toISOString().slice(0, 10), notes: "", receipts: [] });

export const ExpenseSection = ({ targetType, targetId, expenses, onChanged }) => {
    const { canWrite } = useAuth();
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyExpenseForm());
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        if (!canWrite) return;
        api.get("/expenses/categories").then(({ data }) => setCategories(data?.categories || [])).catch(() => { });
    }, [canWrite, expenses.length]);

    const openCreate = () => {
        setEditId(null);
        setForm(emptyExpenseForm());
        setOpen(true);
    };

    const openEdit = (e) => {
        setEditId(e.id);
        setForm({
            category: e.category || "",
            amount: e.amount ?? "",
            paid_by: e.paid_by || "bashari",
            date: e.date || new Date().toISOString().slice(0, 10),
            notes: e.notes || "",
            receipts: e.receipts || [],
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                category: form.category, amount: Number(form.amount),
                paid_by: form.paid_by, date: form.date, notes: form.notes || null,
                receipts: form.receipts || [],
            };
            if (editId) {
                await api.patch(`/expenses/${editId}`, payload);
                toast.success("Expense updated");
            } else {
                await api.post("/expenses", { ...payload, target_type: targetType, target_id: targetId });
                toast.success("Expense added");
            }
            setForm(emptyExpenseForm()); setEditId(null);
            setOpen(false); onChanged();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    const remove = async (eid) => {
        if (!confirm("Delete expense?")) return;
        try { await api.delete(`/expenses/${eid}`); onChanged(); } catch { toast.error("Failed"); }
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-end">
                {canWrite && (
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditId(null); }}>
                    <DialogTrigger asChild>
                        <Button onClick={openCreate} className="rounded-none bg-stone-900 text-stone-50 hover:bg-stone-800" data-testid="add-expense-button">
                            <Plus className="h-4 w-4 mr-2" /> Add expense
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#faf7ef] border-stone-200 rounded-none max-w-lg" data-testid="add-expense-dialog">
                        <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit expense" : "Log expense"}</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <Field label="Category">
                                <Input required list="expense-category-options-detail" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="expense-category-input" placeholder="e.g. Shipping, Gold, Labour" />
                                <datalist id="expense-category-options-detail">
                                    {categories.map(c => <option key={c} value={c} />)}
                                </datalist>
                            </Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Amount (USD)"><Input required type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="expense-amount-input" /></Field>
                                <Field label="Date"><Input required type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="expense-date-input" /></Field>
                            </div>
                            <Field label="Paid by">
                                <Select value={form.paid_by} onValueChange={v => setForm({ ...form, paid_by: v })}>
                                    <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="expense-paidby-select"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none">
                                        {PARTNER_KEYS.map(pk => <SelectItem key={pk} value={pk}>{PARTNER_LABELS[pk]}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Notes"><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-none bg-white border-stone-200" /></Field>
                            <Field label="Invoice / receipt (optional)">
                                <FileUpload
                                    value={form.receipts}
                                    onChange={receipts => setForm({ ...form, receipts })}
                                    accept="image/*,application/pdf"
                                    label="Upload invoice or receipt"
                                    testid="expense-receipts"
                                />
                            </Field>
                            <DialogFooter>
                                <Button type="submit" disabled={saving} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-expense-button">
                                    {saving ? "Saving…" : (editId ? "Save changes" : "Save expense")}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                )}
            </div>
            {expenses.length === 0 ? (
                <div className="border border-stone-200 p-8 text-center bg-[#f5efe0]">
                    <Receipt className="mx-auto h-6 w-6 text-stone-500" strokeWidth={1.2} />
                    <p className="text-sm text-stone-500 mt-3">No expenses yet.</p>
                </div>
            ) : (
                <div className="border border-stone-200" data-testid="target-expenses-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500 text-left">
                                <th className="py-3 px-4">Date</th>
                                <th className="py-3 px-4">Category</th>
                                <th className="py-3 px-4">Paid by</th>
                                <th className="py-3 px-4 text-right">Amount</th>
                                <th className="py-3 px-4">Invoice</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses.map(e => (
                                <tr key={e.id} className="border-b border-stone-200/70 hover:bg-stone-100" data-testid={`target-expense-row-${e.id}`}>
                                    <td className="py-3 px-4 text-stone-500">{fmtDate(e.date)}</td>
                                    <td className="py-3 px-4 text-stone-800">{e.category}<div className="text-[11px] text-stone-500">{e.notes}</div></td>
                                    <td className="py-3 px-4 text-stone-700">{PARTNER_LABELS[e.paid_by]}</td>
                                    <td className="py-3 px-4 text-right num text-rose-600">{fmtUSD(e.amount)}</td>
                                    <td className="py-3 px-4">
                                        {(e.receipts || []).map((f, idx) => (
                                            <FileLink key={f.id || idx} file={f} label={f.original_filename} testid={`target-expense-receipt-${e.id}-${idx}`} />
                                        ))}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        {canWrite && (
                                            <div className="flex items-center justify-end gap-3">
                                                <button onClick={() => openEdit(e)} className="text-stone-400 hover:text-emerald-700" data-testid={`edit-expense-${e.id}`} title="Edit expense">
                                                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                                                </button>
                                                <button onClick={() => remove(e.id)} className="text-stone-400 hover:text-rose-600" data-testid={`delete-expense-${e.id}`} title="Delete expense">
                                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};
